import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import QuizIcon from '@mui/icons-material/Quiz';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import HistoryIcon from '@mui/icons-material/History';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { ProvSetSummary, listSets, getErrorMessage } from '../provApi';

const BRAND_COLOR = '#1a365d';

interface StudentSetListProps {
  onPractice: (setId: string) => void;
  onExam: (setId: string) => void;
}

export default function StudentSetList({ onPractice, onExam }: StudentSetListProps) {
  const [sets, setSets] = useState<ProvSetSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const fetchedSets = await listSets();
        setSets(fetchedSets);
      } catch (err) {
        setError(getErrorMessage(err));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <CircularProgress />
        <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
          Hämtar provset...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>;
  }

  if (sets.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <QuizIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          Inga provset finns ännu
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Be en förälder att skapa ett provset via admin-läget
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography sx={{ fontWeight: 700, mb: { xs: 2, sm: 3 }, fontSize: { xs: '1.3rem', sm: '1.5rem' } }}>
        Välj ett prov att öva på
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 } }}>
        {sets.map((s) => (
          <Card
            key={s._id}
            elevation={1}
            sx={{
              borderRadius: { xs: 2, sm: 3 },
              border: '1px solid',
              borderColor: '#e3e8ef',
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box sx={{ flex: 1, pr: 1 }}>
                  <Typography sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                    {s.title}
                  </Typography>
                  {s.subject && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {s.subject}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {s.type === 'glosor' && (
                      <Chip
                        icon={<MenuBookIcon sx={{ fontSize: 14 }} />}
                        label="Glosor"
                        size="small"
                        sx={{ bgcolor: '#E8F5E9', color: '#2E7D32', fontWeight: 600 }}
                      />
                    )}
                    <Chip
                      label={`${s.questionCount} ${s.type === 'glosor' ? 'ord' : 'frågor'}`}
                      size="small"
                      sx={{ bgcolor: '#e8edf4', color: BRAND_COLOR, fontWeight: 500 }}
                    />
                    {(s.attemptCount || 0) > 0 && (
                      <Chip
                        icon={<HistoryIcon sx={{ fontSize: 16 }} />}
                        label={`${s.attemptCount} försök`}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </Box>
                {s.type === 'glosor'
                  ? <MenuBookIcon sx={{ color: '#2E7D32', fontSize: { xs: 28, sm: 36 }, opacity: 0.7, ml: 1 }} />
                  : <QuizIcon sx={{ color: BRAND_COLOR, fontSize: { xs: 28, sm: 36 }, opacity: 0.6, ml: 1 }} />
                }
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                <Button
                  variant="outlined"
                  startIcon={<VisibilityIcon />}
                  onClick={() => onPractice(s._id)}
                  sx={{
                    flex: 1,
                    minHeight: 48,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    borderColor: BRAND_COLOR,
                    color: BRAND_COLOR,
                  }}
                >
                  Öva
                </Button>
                <Button
                  variant="contained"
                  startIcon={<EditNoteIcon />}
                  onClick={() => onExam(s._id)}
                  sx={{
                    flex: 1,
                    minHeight: 48,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    bgcolor: BRAND_COLOR,
                    '&:hover': { bgcolor: '#0f2744' },
                  }}
                >
                  Gör prov
                </Button>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );
}
